import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import { Checkbox, Flex, Select, Text } from 'core/components';
import AgentDisplay from 'app/views/synthetics/components/AgentDisplay';

export type AgentSelectMode = 'multi' | 'single';

interface AgentSelectProps {
  /** Array of agent options with { value, label, agent } shape */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  agentOptions: any[];
  /** Currently selected agent ID strings. In single mode only the first entry is used. */
  values: string[];
  /** Called with updated array of selected agent ID strings (single mode returns 0 or 1). */
  onChange: (values: string[]) => void;
  /** Selection mode. Defaults to 'multi'. */
  mode?: AgentSelectMode;
  /** Placeholder text. Defaults to 'All Agents' (multi) / 'Select an agent' (single). */
  placeholder?: string;
  /** Cap the number of selectable agents (multi mode). Extra selections are ignored. */
  maxValues?: number;
  /** Truncate selected tags to a single line instead of wrapping (for narrow, inline bars). */
  responsive?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeAgentOptionFormatter(mode: AgentSelectMode) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function agentOptionFormatter(option: any) {
    if (option.groupHeaderProps) {
      return undefined; // let Select render the group header
    }
    const { agent, selected } = option;

    return (
      <Flex alignItems="center" gap={1} overflow="hidden" width="100%">
        {mode === 'multi' && (
          <Checkbox checked={selected} readOnly m={0} style={{ flexShrink: 0, pointerEvents: 'none' }} />
        )}
        <AgentDisplay agent={agent} icon="cloud" showAsn align="spaceBetween" size="small" compact />
      </Flex>
    );
  };
}

// Collapsed field display: one selected agent renders richly; multiple shows a count.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeAgentValueRenderer(agentOptions: any[]) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (selectedOption: any, placeholder: string, values: string[] | string) => {
    // Multi-select tag path: SelectValuesRenderer calls valueRenderer(valueOption) with a
    // single option (no placeholder/values). Render that agent as cloud icon + name (no ASN).
    const { agent: tagAgent } = selectedOption ?? {};
    if (placeholder === undefined && values === undefined && tagAgent) {
      return <AgentDisplay agent={tagAgent} icon="cloud" size="small" compact />;
    }

    let vals: string[] = [];
    if (Array.isArray(values)) {
      vals = values;
    } else if (values) {
      vals = [values];
    }
    if (vals.length === 0) {
      return (
        <Text small muted>
          {placeholder}
        </Text>
      );
    }
    if (vals.length === 1) {
      const opt = agentOptions.find((o) => String(o.value) === String(vals[0]));
      return opt?.agent ? (
        <AgentDisplay agent={opt.agent} icon="cloud" size="small" compact />
      ) : (
        <Text small ellipsis>
          {opt?.label ?? vals[0]}
        </Text>
      );
    }
    return <Text small>{vals.length} agents selected</Text>;
  };
}

const AgentSelect = observer(
  ({ agentOptions, values, onChange, mode = 'multi', placeholder, maxValues, responsive }: AgentSelectProps) => {
    const groupedOptions = useMemo(() => {
      // Bold, non-highlighted section header (override the core gray group-header background).
      const makeHeader = (value: string, label: string) => ({
        value,
        label,
        filterLabel: label,
        isGroupHeader: true,
        groupHeaderProps: { py: '6px', px: '8px', fontWeight: 'bold', fontSize: '11px', bg: 'transparent' }
      });

      // Strip the row hover/selected background — the checkbox is the only selection cue.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const decorate = (opt: any) => ({ ...opt, style: { ...opt.style, background: 'transparent' } });

      // Pin currently-selected agents to the top so they stay visible while scrolling
      const selectedSet = new Set(values);
      const selectedOptions = agentOptions.filter((opt) => selectedSet.has(opt.value));
      const unselectedOptions = agentOptions.filter((opt) => !selectedSet.has(opt.value));

      // Group the remaining (unselected) agents by super_region (geo)
      const regionGroups = new Map<string, typeof agentOptions>();
      const noRegion: typeof agentOptions = [];

      for (const opt of unselectedOptions) {
        const superRegion = opt.agent?.get?.('super_region') as string | undefined;
        if (superRegion) {
          if (!regionGroups.has(superRegion)) {
            regionGroups.set(superRegion, []);
          }
          (regionGroups.get(superRegion) || []).push(opt);
        } else {
          noRegion.push(opt);
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: any[] = [];

      if (selectedOptions.length > 0) {
        result.push(makeHeader('__header_selected', `Selected (${selectedOptions.length})`));
        result.push(...selectedOptions.map(decorate));
      }

      // If the unselected agents don't span multiple regions, list them flat (no region headers)
      const hasRegionGrouping = regionGroups.size > 1 || (regionGroups.size >= 1 && noRegion.length > 0);
      if (!hasRegionGrouping) {
        result.push(...unselectedOptions.map(decorate));
        return result;
      }

      const sortedRegions = Array.from(regionGroups.keys()).sort();
      for (const region of sortedRegions) {
        result.push(makeHeader(`__header_${region}`, region));
        result.push(...(regionGroups.get(region) || []).map(decorate));
      }

      if (noRegion.length > 0) {
        result.push(makeHeader('__header_other', 'Other'));
        result.push(...noRegion.map(decorate));
      }

      return result;
    }, [agentOptions, values]);

    const optionFormatter = useMemo(() => makeAgentOptionFormatter(mode), [mode]);
    const valueRenderer = useMemo(() => makeAgentValueRenderer(agentOptions), [agentOptions]);

    if (mode === 'single') {
      return (
        <Select
          showFilter
          showClearButton
          fill
          menuWidth={350}
          rowHeight={32}
          options={groupedOptions}
          values={values.length ? [values[0]] : []}
          onChange={(selected: string | string[]) => {
            const value = Array.isArray(selected) ? selected[0] : selected;
            onChange(value ? [value] : []);
          }}
          placeholder={placeholder ?? 'Select an agent'}
          optionFormatter={optionFormatter}
          valueRenderer={valueRenderer}
        />
      );
    }

    return (
      <Select
        multi
        keepOpen
        toggle
        showFilter
        showClearButton
        showSelectAll={!maxValues}
        fill
        responsive={responsive}
        menuWidth={350}
        rowHeight={32}
        options={groupedOptions}
        values={values}
        onChange={(next: string[]) => onChange(maxValues && next.length > maxValues ? next.slice(0, maxValues) : next)}
        placeholder={placeholder ?? 'All Agents'}
        optionFormatter={optionFormatter}
        valueRenderer={valueRenderer}
      />
    );
  }
);

export default AgentSelect;
